import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SessionExpiredModal from './SessionExpiredModal';
import sessionManager from '../../utils/sessionManager';
import tokenManager from '../../auth/TokenManager';
import { getSession } from '../../utils/authUtils';
import logger from '../../utils/logger';

const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes
// const IDLE_TIMEOUT = 10 * 1000; // 10 seconds for testing

/**
 * SessionExpiredHandler - Global handler for session expiration
 * This component listens for session expiration events and displays a modal
 * It also monitors user activity and triggers timeout if idle
 */
const SessionExpiredHandler = () => {
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState(undefined);
  const navigate = useNavigate();
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Setup Session Expiration Listener
  useEffect(() => {
    // Handler for session expiration
    const handleSessionExpired = (message) => {
      logger.warn(`[SessionExpiredHandler] Session expired${message ? ` (${message})` : ''}, showing modal`);
      if (message && typeof message === 'string') {
        setModalMessage(message);
      } else {
        setModalMessage(undefined); // Reset to default
      }
      
      // Clear any running idle timer
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      setShowModal(true);
    };

    // Register listener
    sessionManager.onSessionExpired(handleSessionExpired);

    // Cleanup on unmount
    return () => {
      sessionManager.offSessionExpired(handleSessionExpired);
    };
  }, []);

  // Setup Idle Timer
  useEffect(() => {
    const startTimer = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      
      // Only set timer if user is logged in
      // We check localStorage directly for speed
      const hasSession = !!localStorage.getItem('user');
      
      if (hasSession && !showModal) {
        timeoutRef.current = setTimeout(() => {
          const now = Date.now();
          const timeSinceLastActivity = now - lastActivityRef.current;
          
          // Double check if we really are idle (in case of race conditions)
          if (timeSinceLastActivity >= IDLE_TIMEOUT) {
            logger.warn('[SessionExpiredHandler] User idle timeout reached');
            sessionManager.triggerSessionExpired('You have been logged out due to inactivity.');
          } else {
            // If not actually idle (maybe handled by another tab?), restart timer with remaining time
            startTimer();
          }
        }, IDLE_TIMEOUT);
      }
    };

    const handleActivity = () => {
      lastActivityRef.current = Date.now();
      
      // Debounce: only restart timer if we haven't recently
      // We don't need to clear/reset timeout on every pixel of mouse move
      // We just update the lastActivity timestamp.
      // But wait, the setTimeout callback needs to check this timestamp.
      // Actually, to prevent the timeout from firing, we DO need to clear/reset it, 
      // OR verify the timestamp when it fires.
      // Verifying timestamp is more efficient than resetting timer on every event.
      
      // However, if we use the "verify timestamp" approach, the timer will still fire 
      // at the original scheduled time, check timestamp, and if active, reschedule.
      // This is fine.
      
      // But if we want to strictly postpone the check, we can throttle the reset.
      if (!timeoutRef.current) {
        startTimer();
      }
    };
    
    // Polling to check if we need to restart the timer (e.g. after login)
    // or if we should check for idle status periodically?
    // Actually, the simplest robust way is:
    // On activity: update timestamp.
    // Interval every 1 minute: check if (now - lastActivity > timeout).
    // This is much cheaper than managing setTimeout on every event.
    
    const checkIdleInterval = setInterval(() => {
      const hasSession = !!localStorage.getItem('user');
      if (!hasSession || showModal) return;

      const now = Date.now();
      if (now - lastActivityRef.current >= IDLE_TIMEOUT) {
        logger.warn('[SessionExpiredHandler] User idle timeout reached (interval check)');
        sessionManager.triggerSessionExpired('You have been logged out due to inactivity.');
      }
    }, 60000); // Check every minute

    // Attach listeners
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    // Use passive listeners for better scrolling performance
    const listenerOptions = { passive: true };
    
    events.forEach(event => document.addEventListener(event, handleActivity, listenerOptions));

    // Initial start
    handleActivity();

    return () => {
      if (checkIdleInterval) clearInterval(checkIdleInterval);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      events.forEach(event => document.removeEventListener(event, handleActivity));
    };
  }, [showModal]); // Re-run if modal state changes

  const handleClose = () => {
    // Don't allow closing the modal without logging in
    // User must click the login button
  };

  const handleLogin = () => {
    logger.log('[SessionExpiredHandler] User clicked login button');
    
    // Clear expired tokens
    tokenManager.clear('google');
    localStorage.removeItem('googleAccessToken');
    localStorage.removeItem('googleRefreshToken');
    
    // Clear app session too since we are logging out
    // This ensures clean slate
    localStorage.removeItem('user');
    localStorage.removeItem('authToken');
    
    // Reset session expired state
    sessionManager.resetSessionExpired();
    setModalMessage(undefined);
    
    // Close modal
    setShowModal(false);
    
    // Redirect to login page
    navigate('/login');
  };

  return (
    <SessionExpiredModal
      open={showModal}
      onClose={handleClose}
      onLogin={handleLogin}
      message={modalMessage} // Pass the custom message
    />
  );
};

export default SessionExpiredHandler;

